import { useList } from '@refinedev/core';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell, HeroFigure } from '../../../components/Bento.js';
import { DocumentList } from '../../../components/DocumentList.js';

/**
 * The owner's home screen — deliberately narrow.
 *
 * This used to be the portfolio dashboard (properties, units, per-lease
 * balances). Product decision: the owner interface now shows exactly two
 * things — a disbursement actually moving through Stripe, or a document
 * the office has shared — and nothing else, on the reasoning that everything
 * else on the old page was context an owner could check but never act on,
 * where these two are the events that actually mean something changed.
 * Portfolio and resident detail didn't disappear from the app; they moved
 * off the landing page (`/portal/owner/properties/:id`,
 * `/portal/owner/residents`) rather than being deleted, since neither the
 * request behind this rewrite nor the decision that produced it touched
 * those pages specifically.
 *
 * ---------------------------------------------------------------------------
 * Why "in transit" is a count, not a summed dollar figure
 * ---------------------------------------------------------------------------
 *
 * Same invariant the old dashboard's own comment named: money arrives as a
 * decimal string per row, and adding those in JavaScript is how a portal
 * starts disagreeing with the ledger by a cent. Each disbursement's own
 * amount is available on its own row (see OwnerStatementsPage.tsx); the hero
 * here is a count, which is exact by nature.
 *
 * ---------------------------------------------------------------------------
 * What "in transit" means, and why it's TRANSFERRED specifically
 * ---------------------------------------------------------------------------
 *
 * This used to read `Payment.disbursementApprovedAt`, a staff-set visibility
 * flag that predated the real Stripe Connect pipeline and never moved any
 * money — "ready to disburse" meant nothing more than "an admin clicked a
 * button." That flag and its `disbursements:approve` capability have been
 * retired entirely (CLAUDE.md, step 13). This now reads the real
 * `OwnerDisbursement.status`: `TRANSFERRED` is the state between "the Stripe
 * Transfer moved money into this owner's connected account" and "the Payout
 * to their bank completed" — i.e., genuinely in flight, not just approved
 * and not yet paid. Once a row reaches `PAID` (or `PAYOUT_FAILED`) it drops
 * out of this count; the full history and final status live on
 * `OwnerStatementsPage.tsx`'s `payoutStatusLabel()`, which already covers
 * every status this hero doesn't.
 */
interface InTransitDisbursementRow {
  id: string;
  amount: string;
  status: string;
}

export function OwnerHome() {
  const { state } = useSession();

  const { data: disbursements } = useList<InTransitDisbursementRow>({
    resource: 'owner-disbursements',
    filters: [{ field: 'status', operator: 'eq', value: 'TRANSFERRED' }],
    pagination: { mode: 'off' },
  });

  if (state.status !== 'signed-in') return null;

  const rows = disbursements?.data ?? [];

  return (
    <section>
      <h1>Dashboard</h1>
      <p className="muted"></p>

      <Bento>
        <BentoCell span="full">
          <HeroFigure
            label="In transit"
            value={rows.length}
            caption={
              rows.length === 1
                ? 'One disbursement is moving to your bank.'
                : `${rows.length} disbursements are moving to your bank.`
            }
          />
        </BentoCell>

        <BentoCell span="full" title="Documents">
          <p className="bento__hint">Notices and documents the office has shared with you.</p>
          <DocumentList emptyMessage="Nothing to see here." />
        </BentoCell>
      </Bento>
    </section>
  );
}
